import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { ProcessingBarComponent } from '../processing-bar/processing-bar.component';
import { NgIf } from '@angular/common';
import { NotificationBadgeModule } from '../../notification-badge/notification-badge.module';

/**
 * IconButtonComponent is a reusable component that renders an icon inside a button with common styling.
 * It supports optional ARIA labeling, an optional menu trigger, an optional processing bar, an optional badge count, and emits a Clicked event when pressed.
 * The icon is determined by the content projected into the component. Pass the desired icon name inside the component tags.
 * See Angular Material's MatIcon documentation for available icons: https://material.io/resources/icons/
 *
 * @example
 * <crownx-icon-button Id="settings" AriaLabel="Settings" (Clicked)="onSettingsClick()">
 * 	settings
 * </crownx-icon-button>
 *
 * @input Id - Optional unique identifier for the button.
 * @input AriaLabel - Optional ARIA label for accessibility.
 * @input MatMenuTriggerFor - Optional MatMenu to trigger when the button is clicked.
 * @input ShowProcessingBar - Optional flag to show a processing bar.
 * @input BadgeCount - Optional badge count to display on the button.
 * @output Clicked - Emits when the button is clicked.
 */
@Component({
	selector: 'crownx-icon-button',
	standalone: true,
	imports: [
		MatButtonModule,
		MatIconModule,
		MatMenuModule,
		NgIf,
		ProcessingBarComponent,
		NotificationBadgeModule],
	templateUrl: './icon-button.component.html',
	styleUrls: ['./icon-button.component.css']
})
export class IconButtonComponent {
	/* Optional unique identifier for the button. */
	@Input() public Id?: string;
	
	/* Optional ARIA label for accessibility. */
	@Input() public AriaLabel?: string;

	/* Optional MatMenu to trigger when the button is clicked. */
	@Input() public MatMenuTriggerFor?: MatMenu;

	/* Indicates whether to show the processing bar. */
	@Input() public ShowProcessingBar = false;

	/* Optional badge count to display on the button. */
	@Input() public BadgeCount = 0;

	/* Emits when the button is clicked. */
	@Output() public Clicked = new EventEmitter<void>();

	/* Handles the button click event and emits the Clicked event. */
	public OnClick(): void {
		this.Clicked.emit();
	}
}